/* eslint-disable @typescript-eslint/no-unused-vars */
import { BuilderState } from '@/types/builder';
import { AriaAttributes, MouseEventHandler } from 'react';

type Size = 'large' | 'medium' | 'small' | 'smallPadded' | 'tiny';
type Variant = 'primary' | 'secondary' | 'tertiary';

interface LinkButtonProps extends AriaAttributes {
  externalLink?: boolean;
  link: string;
  label: string;
  size: Size;
  variant: Variant;
  className?: string;
  centered?: boolean;
  builderState?: BuilderState;
  builderBlock?: unknown;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
}

/* 
  Destructuring builderState and builderBlock to prevent them being passed 
  onto the DOM element and triggering React warnings. 
  https://legacy.reactjs.org/warnings/unknown-prop.html 
  
  This happens when the component is used on its own in Builder, not within another
  custom component.
*/
export const LinkButton = ({
  externalLink = false,
  link,
  label,
  size,
  variant,
  className = '',
  centered = false,
  builderState,
  builderBlock,
  onClick,
  ...props
}: LinkButtonProps) => {
  const sizeClasses = {
    tiny: 'pt-[8px] pb-[7px] px-[16px] font-bold',
    small: 'pt-[11px] pb-[10px] px-[20px] font-bold',
    smallPadded: 'p-4 font-bold lg:min-w-[270px] min-w-full',
    medium: 'py-4 px-[20px] w-full max-w-[250px] text-[18px] font-bold',
    large: 'pt-[12px] pb-[10px] px-7 md:px-15 w-full max-w-[400px] text-[20px] md:text-[24px] font-black',
  };

  const variants = {
    primary:
      'bg-primary border-primary-400 hover:shadow-xl hover:shadow-primary/20 active:bg-primary-400 text-[white] active:shadow-none group-[.dark]:bg-primary group-[.dark]:text-white group-[.dark]:border-0',
    secondary: 'bg-neutral border-neutral-400 hover:bg-neutral-400',
    tertiary:
      'bg-neutral text-dark-200 border-dark-100 hover:border-primary inline-block active:bg-neutral-400 hover:shadow-lg hover:shadow-primary/10 active:shadow-none group-[.dark]:bg-neutral group-[.dark]:text-dark group-[.dark]:border-0',
  };

  const Button = (
    <a
      href={link}
      target={externalLink ? '_blank' : '_self'}
      rel={externalLink ? 'noopener noreferrer' : undefined}
      onClick={onClick}
      className={`inline-block rounded-[8px] border-1 text-center font-bold transition-all ${sizeClasses[size]} ${variants[variant]} ${className}`}
      {...props}
    >
      {label}
    </a>
  );

  if (centered) {
    return <div className="my-5 flex justify-center">{Button}</div>;
  }

  return Button;
};
